import { AgeGroup } from '@/models';

import { ArrestEditTab, TabConfig } from './types';

// Tab configuration for vertical tabs navigation
export const arrestEditTabs: TabConfig[] = [
  {
    id: ArrestEditTab.ADMINISTRATIVE,
    label: 'Administrative Information',
  },
  {
    id: ArrestEditTab.PERSONAL_INFO,
    label: 'Arrestee Personal Information',
  },
  {
    id: ArrestEditTab.ARREST_INFO,
    label: 'Arrest Information',
  },
  {
    id: ArrestEditTab.OFFENSE_DETAILS,
    label: 'Offense Details',
  },
  {
    id: ArrestEditTab.ASSOCIATED_PERSONS,
    label: 'Associated Persons',
  },
  {
    id: ArrestEditTab.NARRATIVE,
    label: 'Narrative',
  },
  {
    id: ArrestEditTab.SIGNATURES_VERIFICATION,
    label: 'Signatures and Verification',
  },
];

// Field configuration interface
export interface IArrestFormFieldConfig {
  name: string;
  label: string;
  type:
    | 'text'
    | 'number'
    | 'textarea'
    | 'select'
    | 'multiselect'
    | 'checkbox'
    | 'date'
    | 'time'
    | 'datetime'
    | 'autocomplete'
    | 'address'
    | 'toggle'
    | 'readonly'
    | 'signature'
    | 'file'
    | 'searchableSelect';
  placeholder?: string;
  required?: boolean;
  className?: string;
  /**
   * Optional override for the label element className used by `FormFieldRenderer`
   * (does not apply to `type: 'readonly'`, which uses `ReadOnlyField`).
   */
  labelClassName?: string;
  items?: string | { label: string; value: string }[];
  rows?: number;
  disabled?: boolean;
  min?: number;
  showMunicipalityTypeInput?: boolean;
  // For conditional rendering
  conditional?: {
    field: string;
    value: any;
  };
}

// Field group interface for organizing fields with visual separators
export interface IArrestFormFieldGroup {
  fields: IArrestFormFieldConfig[];
}

// Administrative Information tab fields
export const administrativeFields: IArrestFormFieldConfig[] = [
  {
    name: 'nysidNumber',
    label: 'NYSID Number',
    type: 'text',
    className: 'col-span-12 md:col-span-6 lg:col-span-3',
    placeholder: '/',
    disabled: true,
  },
  {
    name: 'obtsNumber',
    label: 'OBTS Number',
    type: 'text',
    className: 'col-span-12 md:col-span-6 lg:col-span-3',
    placeholder: '/',
  },
  {
    name: 'refNumber',
    label: 'Reference Number',
    type: 'text',
    className: 'col-span-12 md:col-span-6 lg:col-span-3',
    placeholder: '/',
  },
  {
    name: 'fbiNumber',
    label: 'FBI Number',
    type: 'text',
    className: 'col-span-12 md:col-span-6 lg:col-span-3',
    placeholder: '/',
    disabled: true,
  },
  {
    name: 'division',
    label: 'Division',
    type: 'text',
    className: 'col-span-12 md:col-span-6 lg:col-span-3',
    placeholder: '/',
  },
];

// Arrestee Personal Information tab fields (all read-only gotten from master name Person fields)
// Organized in groups with visual separators
export const personalInfoFieldGroups: IArrestFormFieldGroup[] = [
  // ===== GROUP 1: All Personal Information Fields =====
  {
    fields: [
      {
        name: 'fullName',
        label: 'Name (Last, First, Middle)',
        type: 'readonly',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'aliasName',
        label: 'Alias / Maiden Name',
        type: 'readonly',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'nickName',
        label: 'Nickname',
        type: 'readonly',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'phoneNumber',
        label: 'Phone Number',
        type: 'readonly',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'sex',
        label: 'Sex',
        type: 'readonly',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'address',
        label: 'Address',
        type: 'readonly',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'subAddress',
        label: 'Sub-address',
        type: 'readonly',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'municipalityType',
        label: 'City, Town, Village',
        type: 'readonly',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'residence',
        label: 'Residence',
        type: 'readonly',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'placeOfBirth',
        label: 'Place of Birth',
        type: 'readonly',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'dateOfBirth',
        label: 'Date of Birth',
        type: 'readonly',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'age',
        label: 'Age',
        type: 'readonly',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'race',
        label: 'Race',
        type: 'readonly',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'ethnicity',
        label: 'Ethnic',
        type: 'readonly',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'skin',
        label: 'Skin',
        type: 'readonly',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'height',
        label: 'Height',
        type: 'readonly',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'weight',
        label: 'Weight',
        type: 'readonly',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'hair',
        label: 'Hair',
        type: 'readonly',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'eyes',
        label: 'Eyes',
        type: 'readonly',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'glasses',
        label: 'Glasses',
        type: 'readonly',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'build',
        label: 'Build',
        type: 'readonly',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'maritalStatus',
        label: 'Marital Status',
        type: 'readonly',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'usCitizen',
        label: 'U.S Citizen',
        type: 'readonly',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'citizenOf',
        label: 'Citizen Of',
        type: 'readonly',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'ssn',
        label: 'Social Security Number',
        type: 'readonly',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'education',
        label: 'Education',
        type: 'readonly',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'religion',
        label: 'Religion',
        type: 'readonly',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'occupation',
        label: 'Occupation',
        type: 'readonly',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'scarsMarksTattoos',
        label: 'Scars / Marks / Tattoos',
        type: 'readonly',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
    ],
  },

  // ===== GROUP 2: Employment Checkbox =====
  {
    fields: [
      {
        name: 'employed',
        label: 'Employed',
        type: 'checkbox',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
        disabled: true,
      },
    ],
  },
];

// Arrest Information tab fields - organized in groups with visual dividers
export const arrestInfoFieldGroups: IArrestFormFieldGroup[] = [
  // ===== GROUP 1: Officer Information =====
  {
    fields: [
      {
        name: 'arrestingOfficer',
        label: 'Arresting Officer',
        type: 'select',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
        items: 'policeUsers',
        required: false,
      },
    ],
  },

  // ===== GROUP 2: Assisting Officer =====
  {
    fields: [
      {
        name: 'assistingOfficer',
        label: 'Assisting Officer',
        type: 'select',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
        items: 'policeUsers',
      },
    ],
  },

  // ===== GROUP 3: Arrest Details, Location, and Classification =====
  {
    fields: [
      // Date/Time/Location
      {
        name: 'arrestDate',
        label: 'Arrest Date',
        type: 'date',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
        placeholder: '/',
        required: false,
      },
      {
        name: 'arrestTime',
        label: 'Time',
        type: 'time',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
        placeholder: '/',
      },
      {
        name: 'arrestLocation',
        label: 'Arrest address',
        type: 'address',
        className: 'col-span-12 lg:col-span-9',
        showMunicipalityTypeInput: true,
      },
      {
        name: 'useIncidentButton',
        label: 'Use incident address',
        type: 'text', // Special handling in renderer for button
        className: 'col-span-12 md:col-span-4 lg:col-span-3',
      },
      {
        name: 'juvenile',
        label: 'Juvenile',
        type: 'select',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
        items: 'NoFurtherYesNo',
      },
      {
        name: 'condition',
        label: 'Condition of Defendant At Arrest',
        type: 'select',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
        items: 'condition',
      },
      {
        name: 'arresteeWeapons',
        label: 'Weapon(s) at Arrest',
        type: 'multiselect',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-6',
        items: 'arresteeWeapons',
      },

      // Co-defendants
      {
        name: 'coDefendantsArrestNumber',
        label: 'Co-defendants Arrest Number',
        type: 'multiselect',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-12',
        items: 'coDefendantsArrestNumber',
      },

      // Miranda Rights
      {
        name: 'miranda',
        label: 'Miranda',
        type: 'select',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
        items: 'yesNo',
      },
      {
        name: 'mirandaBy',
        label: 'Miranda By',
        type: 'select',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
        items: 'policeUsers',
      },
      {
        name: 'mirandaDate',
        label: 'Miranda Date',
        type: 'date',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'mirandaTime',
        label: 'Miranda Time',
        type: 'time',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },

      // Statements & Status
      {
        name: 'statements',
        label: 'Statements',
        type: 'select',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
        items: 'statementsList',
      },
      {
        name: 'courtStatus',
        label: 'Status',
        type: 'select',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
        items: 'courtStatus',
      },

      // Warrant & ID
      {
        name: 'searchWarrant',
        label: 'Search Warrant',
        type: 'select',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
        items: 'yesNo',
      },
      {
        name: 'idProcedure',
        label: 'ID Procedure',
        type: 'select',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
        items: 'idProcedure',
      },

      // Arraignment Court
      {
        name: 'arraignmentCourt',
        label: 'Arraignment Court',
        type: 'searchableSelect',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
        items: 'courtsList',
      },
      {
        name: 'arraignmentJudge',
        label: 'Arraignment Judge',
        type: 'text',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'arraignmentDate',
        label: 'Arraignment Date',
        type: 'date',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'arraignmentTime',
        label: 'Arraignment Time',
        type: 'time',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },

      // Evidence/Property
      {
        name: 'property',
        label: 'Property',
        type: 'select',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
        items: 'yesNo',
        disabled: false,
      },
      {
        name: 'evidence',
        label: 'Evidence',
        type: 'select',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
        items: 'yesNo',
        disabled: false,
      },
      {
        name: 'processedBy',
        label: 'Processed By',
        type: 'select',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
        items: 'policeUsers',
      },

      // Disposition
      {
        name: 'disposition',
        label: 'Disposition',
        type: 'text',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-12',
      },

      // Incident Details
      {
        name: 'incidentNumber',
        label: 'Incident Number',
        type: 'searchableSelect',
        placeholder: '/',
        items: 'closedCfs',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'arrestStatus',
        label: 'Arrestee Status',
        type: 'select',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
        items: 'arrestStatus',
        required: false,
      },
      {
        name: 'bailAmount',
        label: 'Bail Amount ($)',
        type: 'number',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
        min: 0.0,
      },
      {
        name: 'bondsman',
        label: 'Bondsman',
        type: 'text',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },

      // Photo & Arrest Type
      {
        name: 'photoNumber',
        label: 'Photo Number',
        type: 'text',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'arrestType',
        label: 'Arrest Type',
        type: 'select',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
        items: 'arrestType',
        required: false,
      },
      {
        name: 'warrantNumber',
        label: 'Warrant Number',
        type: 'text',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
    ],
  },

  // ===== GROUP 4: Other Agency, Return Details, and Officer Information =====
  {
    fields: [
      // Other Agency Transfer
      {
        name: 'arrestForOtherAgency',
        label: 'Arrest for other agency',
        type: 'checkbox',
        className:
          'col-span-12 md:col-span-6 lg:col-span-3 justify-center items-center m-auto pt-4',
      },
      {
        name: 'otherAgency',
        label: 'Other Agency',
        type: 'select',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-9',
        items: 'agencies',
        conditional: {
          field: 'arrestForOtherAgency',
          value: true,
        },
      },
      {
        name: 'fingerprintPictureTaken',
        label: 'Fingerprint / Picture Taken',
        type: 'select',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
        items: 'yesNo',
      },
      {
        name: 'offenseAddress',
        label: 'Offense address',
        type: 'address',
        className: 'col-span-12 lg:col-span-9',
        showMunicipalityTypeInput: true,
      },

      // Return Details
      {
        name: 'offenseDate',
        label: 'Offense Date',
        type: 'date',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-6',
      },
      {
        name: 'returnCourt',
        label: 'Return Court',
        type: 'searchableSelect',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-6',
        items: 'courtsList',
      },

      // Return Date & Judge
      {
        name: 'returnJudge',
        label: 'Return Judge',
        type: 'text',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'returnDate',
        label: 'Return Date',
        type: 'date',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'returnTime',
        label: 'Return Time',
        type: 'time',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },

      // Defendant Transfer
      {
        name: 'defendantCaseTurnedOverToAgency',
        label: 'Defendant / Case Turned Over to Agency',
        type: 'text',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-12',
      },

      // Officer Name & Booking Details
      {
        name: 'officerName',
        label: 'Officer Name',
        type: 'select',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-4',
        items: 'policeUsers',
      },
      {
        name: 'arresteeTurnedOverTime',
        label: 'Time',
        type: 'time',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-4',
      },
      {
        name: 'arresteeTurnedOverDate',
        label: 'Date',
        type: 'date',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-4',
      },

      // Booking & Medical
      {
        name: 'bookingOfficer',
        label: 'Booking Officer',
        type: 'select',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
        items: 'users',
      },
      {
        name: 'attitude',
        label: 'Arrestee Attitude',
        type: 'text',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'medicalAttention',
        label: 'Arrestee medical attention',
        type: 'text',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'injury',
        label: 'Arrestee Injury/Illness',
        type: 'text',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },

      // Drugs & Juvenile
      {
        name: 'drugsNarcotics',
        label: 'Arrestee drug narcotics',
        type: 'text',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'bloodAlcoholLevel',
        label: 'Arrestee blood alcohol level',
        type: 'text',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
      },
      {
        name: 'juvenileReleaseStatus',
        label: 'Juvenile release status',
        type: 'select',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
        items: 'juvenileReleaseStatus',
      },
      {
        name: 'arrestLarcenyType',
        label: 'Arrest larceny type',
        type: 'select',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
        items: 'arrestLarcenyType',
      },

      // Arrestee Residence & Age Group
      {
        name: 'arresteeResidence',
        label: 'Arrestee residence status',
        type: 'select',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
        items: [
          { label: 'R = Resident', value: 'R' },
          { label: 'N = Nonresident', value: 'N' },
          { label: 'F = Foreign Nonresident', value: 'F' },
          { label: 'U = Unknown', value: 'U' },
        ],
      },
      {
        name: 'arresteeAgeGroup',
        label: 'Arrestee age group',
        type: 'select',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
        items: [
          { label: 'Exact age', value: AgeGroup.EXACT_AGE },
          {
            label: 'Over 98 Years Old',
            value: AgeGroup.OVER_NINETY_EIGHT_YEARS_OLD,
          },
        ],
      },
      {
        name: 'arresteeAge',
        label: 'Arrestee age',
        type: 'number',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
        min: 0,
        conditional: {
          field: 'arresteeAgeGroup',
          value: AgeGroup.EXACT_AGE,
        },
      },

      // Notes
      {
        name: 'arrestNotes',
        label: 'Arrest notes',
        type: 'textarea',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-12',
        rows: 5,
      },

      // Files
      {
        name: 'arrestFiles',
        label: 'Arrest files',
        type: 'file',
        className: 'col-span-12 md:col-span-6 lg:col-span-12',
      },
    ],
  },
];

// Signatures and Verification tab fields - organized in groups with visual dividers
export const signaturesVerificationFieldGroups: IArrestFormFieldGroup[] = [
  // ===== GROUP 1: Arresting Officer Signature =====
  {
    fields: [
      {
        name: 'arrestingOfficerSignature',
        label: 'Arresting Officer Signature',
        type: 'signature',
        className: 'col-span-12 md:col-span-12',
      },
    ],
  },

  // ===== GROUP 2: Supervisor Signature =====
  {
    fields: [
      {
        name: 'supervisorSignature',
        label: 'Supervisor Signature',
        type: 'signature',
        className: 'col-span-12 md:col-span-12',
      },
    ],
  },

  // ===== GROUP 3: SAFIS Verification =====
  {
    fields: [
      {
        name: 'safisLatentPrintIdentificationArrest',
        label:
          'Arrest Made as a Result of A SAFIS Latent Print Identification?',
        type: 'select',
        className: 'col-span-12 md:col-span-12',
        items: [
          {
            label: 'Yes',
            value: 'YES',
          },
          {
            label: 'No',
            value: 'NO',
          },
          {
            label: 'Unknown',
            value: 'UNKNOWN',
          },
        ],
      },
    ],
  },
];

// Offense Details tab fields - organized in groups with visual dividers
export const offenseDetailsGroupFields: IArrestFormFieldGroup[] = [
  // ===== GROUP 1: Law Selection =====
  {
    fields: [
      {
        name: 'offense',
        label: 'Offense',
        type: 'select',
        placeholder: 'Search Penal Codes',
        className: 'col-span-12 md:col-span-12 lg:col-span-3',
        items: 'penalCodes',
      },
    ],
  },

  // ===== GROUP 2: Offense Details =====
  {
    fields: [
      {
        name: 'lawTitle',
        label: 'Title',
        type: 'text',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-2',
        disabled: true,
      },
      {
        name: 'section',
        label: 'Article & Section',
        type: 'text',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-2',
        disabled: true,
      },
      {
        name: 'subsection',
        label: 'Subsections',
        type: 'text',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-2',
        disabled: true,
      },
      {
        name: 'category',
        label: 'Category',
        type: 'text',
        placeholder: '/',
        className: 'col-span-6 md:col-span-6 lg:col-span-1',
        disabled: true,
      },
      {
        name: 'statutoryClass',
        label: 'Class',
        type: 'text',
        placeholder: '/',
        className: 'col-span-6 md:col-span-6 lg:col-span-1',
        disabled: true,
      },
      {
        name: 'degree',
        label: 'Degree',
        type: 'number',
        placeholder: '/',
        className: 'col-span-6 md:col-span-6 lg:col-span-1',
        disabled: true,
        min: 0,
      },
      {
        name: 'nameOfOffense',
        label: 'Name of Offense',
        type: 'text',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
        disabled: true,
      },
      {
        name: 'ncicCode',
        label: 'NCIC Code',
        type: 'text',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
        disabled: true,
      },
      {
        name: 'attemptIndicator',
        label: 'Attempt Indicator',
        type: 'select',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
        items: 'attemptIndicator',
      },
      {
        name: 'count',
        label: 'Counts',
        type: 'number',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
        min: 1,
      },
      {
        name: 'type',
        label: 'Type',
        type: 'select',
        placeholder: '/',
        className: 'col-span-12 md:col-span-6 lg:col-span-3',
        items: [
          { label: 'Appearance Ticket', value: 'APPEARANCE_TICKET' },
          { label: 'Uniform Traffic Ticket', value: 'UNIFORM_TRAFFIC_TICKET' },
          { label: 'Other', value: 'OTHER' },
        ],
      },
    ],
  },
];

// Associated Persons table columns
export const associatedPersonsColumns = [
  { key: 'name', label: 'Name' },
  { key: 'role', label: 'Role' },
  { key: 'relationship', label: 'Relationship' },
];

// Narrative tab fields - organized in groups
export const narrativeFieldGroups: IArrestFormFieldGroup[] = [
  // ===== GROUP 1: Primary Narrative =====
  {
    fields: [
      {
        name: 'primaryNarrative',
        label: 'Narrative',
        type: 'textarea',
        placeholder: 'Enter the primary narrative for this case...',
        className: 'col-span-12',
        rows: 8,
      },
    ],
  },
];
